'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  category: string;
  features: {
    name: string;
    knowmax: boolean | string;
    phygitalmax: boolean | string;
    leapmax: boolean | string;
    devicemax: boolean | string;
  }[];
}

interface ComparisonMatrixProps {
  features: Feature[];
}

export default function ComparisonMatrix({ features }: ComparisonMatrixProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = ['all', ...features.map(f => f.category)];

  const filteredFeatures = selectedCategory === 'all' 
    ? features 
    : features.filter(f => f.category === selectedCategory);

  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mx-auto" />
      ) : (
        <Icon name="XCircleIcon" size={20} variant="solid" className="text-muted-foreground mx-auto opacity-30" />
      );
    }
    return <span className="font-body text-xs text-foreground">{value}</span>;
  };

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <h3 className="font-display font-bold text-2xl text-primary mb-4 md:mb-0">
          Feature Comparison Matrix
        </h3>

        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-md font-body text-sm transition-all duration-300 ${
                selectedCategory === category
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : 'bg-surface text-foreground hover:bg-muted'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-surface">
              <th className="p-4 text-left font-display font-semibold text-sm text-foreground border-b border-border">
                Feature
              </th>
              <th className="p-4 text-center font-display font-semibold text-sm text-foreground border-b border-border">
                KnowMax
              </th>
              <th className="p-4 text-center font-display font-semibold text-sm text-foreground border-b border-border">
                PhygitalMax
              </th>
              <th className="p-4 text-center font-display font-semibold text-sm text-foreground border-b border-border">
                LeapMax
              </th>
              <th className="p-4 text-center font-display font-semibold text-sm text-foreground border-b border-border">
                DeviceMax
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredFeatures.map((category, catIndex) => (
              <>
                <tr key={`cat-${catIndex}`} className="bg-muted">
                  <td colSpan={5} className="p-3 font-display font-semibold text-sm text-primary">
                    {category.category}
                  </td>
                </tr>
                {category.features.map((feature, featIndex) => (
                  <tr
                    key={`feat-${catIndex}-${featIndex}`}
                    className="hover:bg-surface transition-colors duration-200"
                  >
                    <td className="p-4 font-body text-sm text-foreground border-b border-border">
                      {feature.name}
                    </td>
                    <td className="p-4 text-center border-b border-border">
                      {renderCell(feature.knowmax)}
                    </td>
                    <td className="p-4 text-center border-b border-border">
                      {renderCell(feature.phygitalmax)}
                    </td>
                    <td className="p-4 text-center border-b border-border">
                      {renderCell(feature.leapmax)}
                    </td>
                    <td className="p-4 text-center border-b border-border">
                      {renderCell(feature.devicemax)}
                    </td>
                  </tr>
                ))}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}